// Default config and business shapes (API-only; no mock persistence).
// DB pattern: null = use product/business/calendar resolution defaults until the user sets an explicit value.
//
// NOTE ON HARDCODED ENGLISH STRINGS:
// Welcome message, welcome buttons, lead capture text, button text, and lead intent defaults are
// product defaults synced with the backend (webaiwidgetmgr). They are NOT i18n strings — the widget
// always displays these in English as shipped defaults. If the backend defaults change, update here too.

const DEFAULT_BUSINESS = {
  id: 'mock-business-1',
  name: 'My Business',
};

/** Placeholder in welcome message template; replaced with actual business name in store (404 merge). */
const WELCOME_MESSAGE_PLACEHOLDER = '{{businessName}}';

/**
 * Default welcome message template. Use getDefaultWelcomeMessage(businessName) to get the message
 * with business name substituted (e.g. in store when building config on 404).
 */
const DEFAULT_WELCOME_MESSAGE_TEMPLATE = `Hi,
Welcome to {{businessName}}.
How can I help you today?`;

/**
 * Default welcome buttons shown under the welcome message.
 */
const DEFAULT_WELCOME_BUTTONS = [
  { label: 'Book an appointment', message: "I'd like to book an appointment" },
  { label: 'Ask about services', message: 'What services do you offer?' },
  { label: 'Query opening hours', message: 'What are your hours?' },
];

/**
 * Build the default welcome message with business name inserted.
 * @param {string} businessName - Business display name (e.g. from platform API or DEFAULT_BUSINESS.name)
 * @returns {string}
 */
function getDefaultWelcomeMessage(businessName) {
  const name = businessName || DEFAULT_BUSINESS.name;
  return DEFAULT_WELCOME_MESSAGE_TEMPLATE.split(WELCOME_MESSAGE_PLACEHOLDER).join(name);
}

/**
 * Product default copy for lead capture when DB has null (same string server/widget use for resolution).
 */
const DEFAULT_LEAD_CAPTURE_TEXT = 'Details are shared so we can follow up, we will call you back as soon as we are able to, usually within 24 hours';
const LEAD_INTENT_GENERAL = 'General inquiry';
const DEFAULT_LEAD_DATA_ITEMS = 'Name, Phone number, Email(Optional), Reason for inquiry';
const DEFAULT_LEAD_MESSAGE_TO_VISITOR = DEFAULT_LEAD_CAPTURE_TEXT;
const NEW_LEAD_INTENT_PLACEHOLDER = 'Add intent here';
const NEW_LEAD_DATA_ITEMS_PLACEHOLDER = 'Name, Phone number, Email(Optional), Reason for inquiry';

/** Floating button label when `button_text` is null in DB. */
export const DEFAULT_BUTTON_TEXT = 'Contact / FAQ';

/** Picker fallback when business brand color is unavailable. */
export const DEFAULT_BRAND_COLOR_FALLBACK = '#3B82F6';

/**
 * Legacy neutral gray; **default text on brand** uses portal parity via `contrastTextColor`
 * (Core `ColorBrightness` / `brand_text_contrast`), not this constant.
 */
export const DEFAULT_TEXT_COLOR = '#333333';

/** Default auto-popup delay (seconds) when `auto_popup_delay` is null in DB. */
export const DEFAULT_AUTO_POPUP_DELAY_SECONDS = 5;

/**
 * In-memory / first-persist shape: optional fields are null so the API stores NULL until customized.
 */
const DEFAULT_CONFIG = {
  id: '',
  business_id: '',
  widget_title: null,
  faq_and_guidance: null,
  welcome_message: null,
  brand_color: null,
  text_color: null,
  logo_url: null,
  actions: null,
  auto_popup_enabled: null,
  auto_popup_delay: null,
  welcome_buttons: null,
  lead_intent_data: null,
  lead_capture_text: null,
  button_text: null,
  include_contact_info: null,
  include_services: null,
  include_products: null,
  include_booking_policies: null,
  include_terms_of_service: null,
  guidance_urls: null,
};

/**
 * Paths to edit guidance sources in the app (Angular/settings). Used for "edit" links next to checkboxes.
 * Values can be a path string or { path, queryParams } so Angular receives query params separately and
 * can merge them (e.g. with section=website-widgets) without producing a malformed URL.
 */
const GUIDANCE_EDIT_PATHS = {
  contact_info: '/app/settings/business',
  services: '/app/settings/services',
  products: '/app/settings/products',
  booking_policies: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
  terms_of_service: { path: '/app/settings/services', queryParams: { tab: 'settings', active_tab: 1 } },
};

/** Keys for the guidance inclusion checkboxes (Content tab). */
const GUIDANCE_CHECKBOX_KEYS = [
  'include_contact_info',
  'include_services',
  'include_products',
  'include_booking_policies',
  'include_terms_of_service',
];

/** Calendar settings → General availability (same as Vuetage `navigateTo('app.calendar_settings', { tab: 'GeneralAvailability' })`). */
const CALENDAR_GENERAL_AVAILABILITY_SETTINGS = {
  path: '/app/settings/calendar_settings',
  queryParams: { tab: 'GeneralAvailability' },
};

/**
 * Build the full HTML page for the widget preview iframe.
 * Kept here to keep the preview component focused on layout/lifecycle.
 * @param {object} opts
 * @param {string} opts.websiteUrl - Normalised website URL (or '' for placeholder)
 * @param {string} opts.widgetId
 * @param {string} opts.chatServiceUrl
 * @param {string} opts.scriptUrl - Widget JS entry point
 * @param {string} opts.desktopCssUrl
 * @param {string} opts.mobileCssUrl
 * @param {string} opts.widgetMode - 'desktop' | 'mobile'
 * @param {string} opts.placeholderText - Translated placeholder shown when no website URL
 * @returns {string} Complete HTML document
 */
function buildPreviewPageHtml({
  websiteUrl, widgetId, chatServiceUrl, scriptUrl,
  desktopCssUrl, mobileCssUrl, widgetMode, placeholderText,
}) {
  const w = JSON.stringify(websiteUrl);
  const wi = JSON.stringify(widgetId);
  const c = JSON.stringify(chatServiceUrl);
  const s = JSON.stringify(scriptUrl);
  const dCss = JSON.stringify(desktopCssUrl);
  const mCss = JSON.stringify(mobileCssUrl);
  const mode = JSON.stringify(widgetMode);
  const pt = JSON.stringify(placeholderText);

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Widget Preview</title>
<style>
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; overflow: hidden; height: 100%; }
#preview-background { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; display: block; z-index: 0; }
#preview-placeholder { position: absolute; inset: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f0f0f0; color: #999; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 24px; font-weight: 500; z-index: 0; }
</style>
</head>
<body>
<div id="preview-placeholder"></div>
<iframe id="preview-background" title="Website background" sandbox="allow-scripts allow-same-origin allow-forms allow-popups"></iframe>
<script>
(function() {
  var websiteUrl = ${w};
  var placeholder = document.getElementById('preview-placeholder');
  var bg = document.getElementById('preview-background');
  if (websiteUrl) {
    placeholder.style.display = 'none';
    bg.style.display = 'block';
    try {
      var u = new URL(websiteUrl);
      if (u.protocol === 'https:' || u.protocol === 'http:') {
        var host = (u.hostname || '').toLowerCase();
        var sameOrigin = host === window.location.hostname.toLowerCase();
        if (!sameOrigin) bg.src = websiteUrl;
      }
    } catch (e) {}
  } else {
    placeholder.textContent = ${pt};
    bg.style.display = 'none';
  }
  var widgetId = ${wi};
  var chatServiceUrl = ${c};
  var scriptUrl = ${s};
  var desktopCssUrl = ${dCss};
  var mobileCssUrl = ${mCss};
  var cacheBuster = Date.now();
  var bust = function(url) { return url ? url + (url.indexOf('?') !== -1 ? '&' : '?') + '_t=' + cacheBuster : ''; };
  if (widgetId && chatServiceUrl && scriptUrl) {
    var initOpts = { id: widgetId, chatServiceUrl: chatServiceUrl, configCacheBuster: cacheBuster, fromWidgetEditor: true, mode: ${mode} };
    if (desktopCssUrl) initOpts.desktopCss = bust(desktopCssUrl);
    if (mobileCssUrl) initOpts.mobileCss = bust(mobileCssUrl);
    window.liveSiteAsyncInit = function() { LiveSite.init(initOpts); };
    var el = document.createElement('script');
    el.src = bust(scriptUrl);
    el.async = true;
    document.body.appendChild(el);
  }
})();
\x3c/script>
</body>
</html>`;
}

export {
  DEFAULT_CONFIG,
  DEFAULT_LEAD_CAPTURE_TEXT,
  LEAD_INTENT_GENERAL,
  DEFAULT_LEAD_DATA_ITEMS,
  DEFAULT_LEAD_MESSAGE_TO_VISITOR,
  NEW_LEAD_INTENT_PLACEHOLDER,
  NEW_LEAD_DATA_ITEMS_PLACEHOLDER,
  DEFAULT_BUSINESS,
  getDefaultWelcomeMessage,
  DEFAULT_WELCOME_BUTTONS,
  GUIDANCE_EDIT_PATHS,
  GUIDANCE_CHECKBOX_KEYS,
  CALENDAR_GENERAL_AVAILABILITY_SETTINGS,
  buildPreviewPageHtml,
};
